import { stripTags } from '@/content/story';
import { qa } from '@/qa/recorder';
import type {
  CoachOptions,
  CoachPart,
  CoachTurn,
  CoachWord,
  Exercise,
  SelfCheck,
  Hint,
  Outcome,
  PracticeTarget,
  Transmission,
  TransmitResult,
} from '@/game/coach/coach';
import { Coach } from '@/game/coach/coach';
import { NOISE_ANSWERS } from '@/game/coach/noiseCheck';
import { matchChoice } from '@/speech/understand/match';
import type { AgeBand } from '@/game/coach/phrases';
import type { ScoreResult, SpeechScorer } from '@/speech/types';
import type { Utterance } from '@/speech/audio/listener';
import { splitTries } from '@/speech/dsp/split';
import type { VoicePart } from '@/audio/voice';

/**
 * Runs a spoken practice session in real time: microphone on continuously, every utterance scored
 * and answered by the coach with spoken feedback, until the microphone is turned off, the child is
 * inactive, or the session ends. Handles turn-taking: the child may barge in while the app talks
 * (the app stops), the child's own recording is never re-heard as an attempt, and silence leads to
 * gentle re-prompts and finally a pause. Framework-free; React subscribes to snapshots.
 */

export type SessionPhase =
  /** The speech model is downloading or starting. */
  | 'loading'
  /** Microphone off; the child taps the mic to start. */
  | 'off'
  /** Opening the microphone. */
  | 'starting'
  /** The app is speaking. */
  | 'talking'
  /** The child's turn: waiting for speech. */
  | 'listening'
  /** Speech in progress. */
  | 'hearing'
  /** Scoring the attempt. */
  | 'checking'
  /** The self-check is open: the child answers by tapping. */
  | 'answering'
  | 'complete'
  | 'error';

export interface SessionSnapshot {
  phase: SessionPhase;
  /** Model download progress in [0, 1]. */
  loadProgress: number;
  word: CoachWord;
  target: PracticeTarget;
  /** Latest coach line, also spoken. */
  caption: string;
  /** Mouth diagram for the latest placement cue, shown until the next judged attempt. */
  hint: Hint | null;
  /** How the word on screen is asked for (picture, sentence, riddle). */
  exercise: Exercise;
  /** Next words to choose from (by saying or tapping one), or null. */
  choices: readonly CoachWord[] | null;
  /** Self-check question after playback (tap the word it sounded like), or null. */
  selfCheck: SelfCheck | null;
  /** The noise question is open: is a quieter spot available? (answerNoise; noiseCheck.ts) */
  noiseQuestion: boolean;
  /**
   * The open question's answers can be tapped: only once the child has heard what they judge
   * (their playback, or the helper's word), so they listen before they choose.
   */
  answerable: boolean;
  /** The step of the cue on the mouth picture being said (Hint.steps), or null. */
  hintStep: number | null;
  /** The child makes this sound now, as a step of the cue ("ee"), or null. */
  trying: string | null;
  lastOutcome: Outcome | null;
  /** Scores of the latest judged attempt at the current word or syllable. */
  result: ScoreResult | null;
  /** Rewards earned this session. */
  successes: number;
  progressCredits: number;
  /** Increments with every reward (for animations). */
  rewardSerial: number;
  lastReward: 'success' | 'progress' | null;
  /** The transmission stage is on (after the mission): the child says the codename to Scout. */
  transmitting: boolean;
  /** How the transmission ended (a clear r earns the bonus star), or null. */
  transmitted: TransmitResult | null;
  error: string | null;
}

/** What the session needs from the listening pipeline (see speech/audio/listener.ts). */
/** How often the background level is checked while the child's turn is open (ms). */
const NOISE_CHECK_EVERY_MS = 1000;
/** Words in a spoken answer to a question, at most. */
const MAX_ANSWER_WORDS = 5;

export interface ListenerControl {
  /** `markSpeech` false: see Listener.setPrompting (the end of a resumed line). */
  setPrompting(prompting: boolean, markSpeech?: boolean): void;
  setHangover(ms: number): void;
  setMuted(muted: boolean): void;
  /** A voice is being heard right now, possibly a word just starting. */
  hearingVoice?(): boolean;
  /** The steady background level (dBFS), for the noise check. */
  noiseFloorDb?(): number;
  close(): Promise<void>;
}

export interface ListenerCallbacks {
  /** See ListenerEvents.onSpeechStart. */
  onSpeechStart: (bargeIn: boolean, childFirst?: boolean) => void;
  onUtterance: (utterance: Utterance) => void;
  onDiscarded: () => void;
  onLevel: (level: number) => void;
}

export interface SessionVoice {
  play(
    parts: readonly VoicePart[],
    hooks?: {
      onPlayback?: (playing: boolean) => void;
      onPart?: (part: VoicePart, seconds?: number) => void;
    },
  ): Promise<'done' | 'interrupted'>;
  stop(): void;
}

export interface SessionDeps {
  scorer: SpeechScorer;
  voice: SessionVoice;
  /** Opens the audio input and starts listening; called from the mic button (a user gesture). */
  startListening: (callbacks: ListenerCallbacks) => Promise<ListenerControl>;
  words: readonly CoachWord[];
  sound: { phoneme: string; label: string };
  ageBand?: AgeBand;
  /** Score as a young voice (8 and under): see YOUNG_ACCEPT_THRESHOLD. */
  young?: boolean;
  /** Silence before a re-prompt, in ms. */
  silenceMs?: number;
  /** Speech-to-text for spoken answers to questions (loaded on first use), when available. */
  transcribe?: (audio: Float32Array) => Promise<string>;
  /** Starts loading the speech-to-text, ahead of a question. */
  prepareAnswers?: () => void;
  random?: () => number;
  /** Sentence and riddle clues per word (content/exercises.ts). */
  exercises?: CoachOptions['exercises'];
  /** Let the child choose every other next word (CoachOptions.choices). */
  choices?: boolean;
  /** How the mission opens (CoachOptions.briefing). */
  briefing?: CoachOptions['briefing'];
  /** Set when the browser cannot listen or score (missing features): shown instead of loading. */
  unsupported?: string;
}

/** Wait for a response before a gentle re-prompt (children need 3-5 s of wait time). */
export const DEFAULT_SILENCE_MS = 5000;
/**
 * Children often say a word twice when the app does not react at once. After a miss the feedback
 * waits this long for a repeat; the better of the two attempts is judged.
 */
export const REPEAT_WAIT_MS = 700;
/**
 * A repeat still being said when that wait ends is waited for (checked this often, up to this
 * long), so it is judged instead of lost. QA 2026-10-11: a clear "rah" said again just after a
 * noisy first try was dropped, and the noise was played back as the child's attempt.
 */
const REPEAT_POLL_MS = 250;
export const REPEAT_WAIT_MAX_MS = 3000;
/** Speech starting this soon after an accepted attempt is a happy repeat: it is ignored. */
export const REPEAT_WINDOW_MS = 1500;
/**
 * An utterance this much quieter than the child's usual attempts is background (a TV, people
 * further away; QA simulated users measured about 15-20 dB) and is never judged.
 */
export const BACKGROUND_BELOW_CHILD_DB = 14;
/**
 * Speech that starts over the app's own voice is judged only when it is about as loud as the child:
 * a child talking over the app speaks up (simulated users: -16 to -20 dB peaks, child level about
 * -17.5), while the app's voice leaking back into the microphone is quieter (-22 to -29 dB) and,
 * being a clean model of the target, would otherwise be accepted as a correct /r/.
 */
export const BARGE_IN_BELOW_CHILD_DB = 8;
/**
 * Speech that starts this soon after the app stops talking gets the same level check: the end of
 * the app's own line (often the model word itself) can reach the microphone just after the app
 * falls silent (simulated TV room: "fire" at -28 dB, 9 dB under the child, accepted as correct).
 */
export const ECHO_TAIL_MS = 500;
/**
 * Turn-taking: the microphone is ignored while the app talks. On a phone the loudspeaker sits next
 * to the microphone and the browser's echo cancellation does not remove the app's own voice
 * (iPhone Safari, 2026-10-11 QA session: echo at -17 to -30 dB with the child at -13), so
 * listening while talking made the app interrupt and re-prompt itself. Right after it stops, its
 * reverberation is left to the ECHO_TAIL_MS level check, so a quick answer is not cut off.
 */
/**
 * The child's loudness before any attempt was heard: a typical close-talking peak with the
 * browser's gain control (frame power, dBFS). Without it, a TV talking before the child's first
 * word is judged as the child. To be checked against real test sessions (they log every level).
 */
export const CHILD_PEAK_PRIOR_DB = -20;
/** After this many background utterances in a row, a hint asks the child to come closer. */
export const BACKGROUND_HINT_AFTER = 2;
/** A cut utterance (seconds long) peaking less than this above the floor is steady noise. */
export const STEADY_NOISE_SNR_DB = 15;

export class PracticeSession {
  private readonly deps: SessionDeps;
  private coach: Coach;
  private snapshot: SessionSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly levelListeners = new Set<(level: number) => void>();
  private listener: ListenerControl | undefined;
  private noiseCheckedAt = 0;
  private silenceTimer: ReturnType<typeof setTimeout> | undefined;
  private loaded = false;
  private micOn = false;
  /** Bumped whenever the mic stops, so late async results are ignored. */
  private epoch = 0;
  private lastAudio: Float32Array | undefined;
  private talking = false;
  /** Bumped by every spoken turn, so an interrupted turn knows a newer one took over. */
  private turnSerial = 0;
  /** When the latest feedback turn and the latest speech began (performance.now()). */
  private turnStartedAt = 0;
  private speechStartedAt = 0;
  /** When the latest attempt arrived and whether it was accepted (to recognise repeats). */
  private lastAttempt: { at: number; accepted: boolean } | undefined;
  /** A missed attempt held for REPEAT_WAIT_MS in case the child says the word again. */
  private held:
    { result: ScoreResult; utterance: Utterance; timer: ReturnType<typeof setTimeout> } | undefined;
  /**
   * The current speech is not an attempt: a repeat of an accepted attempt (it does not cut the
   * praise) or speech over a resumed turn (most likely the same echo again).
   */
  private ignoringRepeat = false;
  /** The turn being spoken: its voice parts and how many have started. */
  private playing: { parts: VoicePart[]; started: number } | undefined;
  /** Timers that page a long line's caption sentence by sentence while it is said. */
  private captionTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * Shows a line as the caption. A line longer than the caption box shows sentence by sentence
   * (pages of at most CAPTION_PAGE characters), each page timed to its share of the line's length
   * (from the recording, or estimated at SPEECH_CHARS_PER_S for the browser voice).
   */
  private followCaption(text: string, seconds?: number) {
    for (const t of this.captionTimers) clearTimeout(t);
    this.captionTimers = [];
    const pages = captionPages(text);
    this.update({ caption: pages[0] ?? text });
    const total = (seconds ?? text.length / SPEECH_CHARS_PER_S) * 1000;
    let at = 0;
    pages.slice(0, -1).forEach((page, i) => {
      at += (page.length / text.length) * total;
      const next = pages[i + 1] ?? '';
      this.captionTimers.push(
        setTimeout(() => {
          this.update({ caption: next });
        }, at),
      );
    });
  }
  /**
   * What a barge-in cut off. If the "child" turns out to be the app's own echo or noise, the app
   * resumes from the interrupted line instead of skipping the rest of its feedback.
   */
  private interrupted: VoicePart[] | undefined;
  /**
   * Speaking a resumed turn: speech over it does not cut it off (the same echo would again), but
   * is still judged when it is at the child's level (`BARGE_IN_BELOW_CHILD_DB`), so a child who
   * answers over the resumed line is not lost.
   */
  private resuming = false;
  /** A cue step in progress: the child makes a sound, then these parts follow. */
  private trying: { rest: CoachPart[]; epoch: number } | undefined;
  /** When the app last stopped talking (performance.now()). */
  private talkEndedAt = -Infinity;
  /** Why the listener is muted: the app talking (until LISTEN_AFTER_TALK_MS after) or playback. */
  private mutedForTalk = false;
  private mutedForPlayback = false;
  /** The child's usual loudness (utterance peak, dBFS), learned from heard attempts. */
  private childPeakDb = CHILD_PEAK_PRIOR_DB;
  /** When the current wait for the child ends in a re-prompt (performance.now()). */
  private silenceDeadline = 0;
  /** Quiet utterances ignored as background in a row. */
  private backgroundDrops = 0;
  /** The target whose overlapped miss was not counted (once per target). */
  private overlapRetried: string | undefined;

  constructor(deps: SessionDeps) {
    this.deps = deps;
    this.coach = this.newCoach();
    this.snapshot = {
      phase: 'loading',
      loadProgress: 0,
      word: this.coach.word,
      target: this.coach.target,
      caption: '',
      hint: null,
      exercise: this.coach.exercise,
      choices: null,
      selfCheck: null,
      noiseQuestion: false,
      answerable: false,
      hintStep: null,
      trying: null,
      lastOutcome: null,
      result: null,
      successes: 0,
      progressCredits: 0,
      rewardSerial: 0,
      lastReward: null,
      transmitting: false,
      transmitted: null,
      error: null,
    };
  }

  // --- subscription (useSyncExternalStore) ---
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SessionSnapshot => this.snapshot;

  /** Input level updates (about 50 per second), kept out of snapshots to avoid re-renders. */
  onLevel(listener: (level: number) => void): () => void {
    this.levelListeners.add(listener);
    return () => this.levelListeners.delete(listener);
  }

  /** Downloads and starts the model; resolves to whether it is ready. */
  async load(): Promise<boolean> {
    if (this.deps.unsupported) {
      this.update({ phase: 'error', error: this.deps.unsupported });
      return false;
    }
    try {
      await this.deps.scorer.load((loaded, total) => {
        // Download callbacks arrive per chunk: update the screen only per whole percent, and never
        // backwards (files are counted as they start), so it neither re-renders nor jumps.
        const progress = Math.floor((total > 0 ? loaded / total : 0) * 100) / 100;
        if (progress > this.snapshot.loadProgress) this.update({ loadProgress: progress });
      });
      this.loaded = true;
      this.update({ phase: this.micOn ? this.snapshot.phase : 'off', loadProgress: 1 });
      return true;
    } catch (error) {
      this.update({ phase: 'error', error: `The speech model could not load: ${String(error)}` });
      return false;
    }
  }

  /** How the mission went so far (targets, correct, correct at the first try), for stars. */
  summary(): ReturnType<Coach['summary']> {
    return this.coach.summary();
  }

  get isMicOn(): boolean {
    return this.micOn;
  }

  /** The mic button. */
  async toggleMic(): Promise<void> {
    if (this.micOn) await this.stopMic();
    else await this.startMic();
  }

  async startMic(): Promise<void> {
    if (this.micOn) return;
    if (!this.loaded) {
      // The model failed to load earlier (e.g. offline on first use): tapping the mic retries.
      if (this.snapshot.phase !== 'error') return;
      this.update({ phase: 'loading', error: null });
      if (!(await this.load())) return;
    }
    if (this.snapshot.phase === 'complete') this.restart();
    this.micOn = true;
    qa.event('mic', { on: true });
    const epoch = ++this.epoch;
    this.update({ phase: 'starting', error: null });
    try {
      const listener = await this.deps.startListening({
        onSpeechStart: (bargeIn, childFirst) => {
          this.speechStarted(bargeIn, epoch, childFirst);
        },
        onUtterance: (u) => {
          void this.utterance(u, epoch);
        },
        onDiscarded: () => {
          if (epoch !== this.epoch) return;
          this.ignoringRepeat = false;
          this.notAnAttempt(epoch);
        },
        onLevel: (level) => {
          for (const l of this.levelListeners) l(level);
          this.checkNoise(epoch);
        },
      });
      if (epoch !== this.epoch) {
        await listener.close();
        return;
      }
      this.listener = listener;
      await this.say(this.coach.prompt(), epoch);
    } catch (error) {
      this.micOn = false;
      this.update({
        phase: 'error',
        error: `The microphone could not start. Check that the browser may use it. (${String(error)})`,
      });
    }
  }

  async stopMic(): Promise<void> {
    if (this.micOn) qa.event('mic', { on: false });
    this.micOn = false;
    this.epoch++;
    this.clearSilence();
    this.takeHeld();
    this.ignoringRepeat = false;
    this.interrupted = undefined;
    this.trying = undefined;
    // Turning the microphone off with the noise question open answers it: keep going here.
    if (this.snapshot.noiseQuestion) {
      this.coach.answerNoise(false);
      this.update({ noiseQuestion: false, answerable: false });
    }
    this.deps.voice.stop();
    this.endTalking();
    this.mutedForPlayback = false;
    const listener = this.listener;
    this.listener = undefined;
    const keep: SessionPhase[] = ['complete', 'error', 'loading'];
    if (!keep.includes(this.snapshot.phase)) this.update({ phase: 'off', trying: null });
    await listener?.close();
  }

  /** The "hear the word" button: a model of the current target (also during the child's turn). */
  /** Picks the offered word an attempt matches best, and makes it the current word. */
  private async resolveChoice(audio: Float32Array) {
    const options = this.coach.choices ?? [];
    let best: { id: string; fit: number } | undefined;
    for (const word of options) {
      const r = await this.deps.scorer.score({
        audio,
        targetPhonemes: word.phonemes,
        kind: word.kind ?? 'word',
        ...(this.deps.young ? { young: true } : {}),
      });
      const fit = r.phonemes.reduce((n, p) => n + p.score, 0) / Math.max(1, r.phonemes.length);
      if (!best || fit > best.fit) best = { id: word.id, fit };
    }
    if (best) this.applyChoice(best.id, 'voice');
  }

  private applyChoice(id: string, by: 'voice' | 'tap'): boolean {
    if (!this.coach.choose(id)) return false;
    qa.event('note', { note: `chose ${id} by ${by}` });
    this.update({
      word: this.coach.word,
      target: this.coach.target,
      exercise: this.coach.exercise,
      choices: null,
      result: null,
    });
    return true;
  }

  /** The child answered the self-check by tapping a word. */
  async answerSelfCheck(answer: string): Promise<void> {
    if (!this.snapshot.answerable) return;
    const turn = this.coach.answerSelfCheck(answer);
    if (!turn) return;
    qa.event('note', { note: `self-check: ${answer}` });
    if (this.micOn) await this.say(turn, this.epoch);
    else this.update({ selfCheck: null });
  }

  /**
   * The child's answer to the noise question: a quieter spot is available and they go there
   * (the microphone pauses until they tap it again), or not (practice goes on here).
   */
  async answerNoise(moving: boolean): Promise<void> {
    if (!this.snapshot.answerable || !this.snapshot.noiseQuestion) return;
    const turn = this.coach.answerNoise(moving);
    if (!turn) return;
    qa.event('note', { note: `noise: ${moving ? 'moving' : 'staying'}` });
    if (this.micOn) await this.say(turn, this.epoch);
    else this.update({ noiseQuestion: false, answerable: false });
  }

  private async spokenNoiseAnswer(audio: Float32Array, epoch: number) {
    const transcribe = this.deps.transcribe;
    if (!transcribe) return;
    const text = await transcribe(audio).catch(() => '');
    if (epoch !== this.epoch || !this.snapshot.noiseQuestion) return;
    // An answer is a few words; more is someone talking nearby.
    if (text.split(/\s+/).filter(Boolean).length > MAX_ANSWER_WORDS) return;
    const pick = matchChoice(text, [
      { id: 'moving', says: NOISE_ANSWERS.moving.says },
      { id: 'stay', says: NOISE_ANSWERS.stay.says },
    ]);
    qa.event('note', { note: `noise answer heard "${text}": ${pick ?? 'unclear'}` });
    if (pick) await this.answerNoise(pick === 'moving');
  }

  /** About once a second while the child's turn is open: has the background stayed loud? */
  private checkNoise(epoch: number) {
    const now = performance.now();
    if (now - this.noiseCheckedAt < NOISE_CHECK_EVERY_MS) return;
    this.noiseCheckedAt = now;
    if (epoch !== this.epoch || this.snapshot.phase !== 'listening') return;
    // Not during a cue step's sound, nor over a word that is just starting.
    if (this.trying || this.listener?.hearingVoice?.()) return;
    const db = this.listener?.noiseFloorDb?.();
    if (db === undefined) return;
    const turn = this.coach.noiseFloor(db);
    if (turn) {
      qa.event('note', { note: `noisy background: floor ${db.toFixed(1)} dBFS` });
      void this.say(turn, epoch);
    }
  }

  /** The child tapped one of the offered next words. */
  async choose(id: string): Promise<void> {
    if (this.snapshot.phase === 'hearing' || this.snapshot.phase === 'checking') return;
    if (!this.applyChoice(id, 'tap')) return;
    if (this.micOn) await this.say(this.coach.prompt(), this.epoch);
  }

  /**
   * The transmission stage (epic #44), after a mission that uncovered the whole codename: Scout
   * asks for the codename and the child says it. Ends `complete` again, with `transmitted` set.
   */
  async transmit(target: Transmission): Promise<void> {
    if (this.snapshot.transmitting || this.snapshot.phase !== 'complete') return;
    this.coach.transmit(target);
    // A new stage: the codename is not a happy repeat of the mission's last word.
    this.lastAttempt = undefined;
    this.update({
      // Not 'complete' any more, so starting the mic keeps this coach (it would restart one).
      phase: 'off',
      transmitting: true,
      target: this.coach.target,
      result: null,
      hint: null,
      caption: '',
      choices: null,
      selfCheck: null,
      noiseQuestion: false,
    });
    await this.startMic();
  }

  /** Sends the codename without saying it (the way through without a voice): no bonus star. */
  async sendCodename(): Promise<void> {
    if (!this.snapshot.transmitting || this.snapshot.transmitted) return;
    if (this.snapshot.phase === 'hearing' || this.snapshot.phase === 'checking') return;
    await this.say(this.coach.sendCodename(), this.epoch);
  }

  /** The child put the self-check away without answering ("skip"): back to the word. */
  async dismissQuestion(): Promise<void> {
    const turn = this.coach.dismissQuestion();
    if (!turn) return;
    qa.event('note', { note: 'question skipped' });
    if (this.micOn) await this.say(turn, this.epoch);
    else this.update({ selfCheck: null, noiseQuestion: false, answerable: false });
  }

  // --- debug (features/debug: the level's debug bar) ---

  /**
   * Debug: an attempt judged as if the child had said the target with this /r/ score (and, when
   * missed, heard as `heardAs`, e.g. "w"), without the microphone or the scorer.
   */
  async debugAttempt(targetScore: number, heardAs?: string): Promise<void> {
    if (this.snapshot.phase === 'checking' || this.snapshot.phase === 'complete') return;
    if (this.coach.finished && !this.snapshot.transmitting) return;
    // A choice is open: the attempt says the first word offered (as a spoken attempt would).
    const offered = this.coach.choices?.[0];
    if (offered) this.applyChoice(offered.id, 'tap');
    const target = this.coach.target;
    const threshold = target.kind === 'syllable' ? 0.3 : 0.75;
    const index = Math.max(0, target.phonemes.indexOf('ɹ'));
    const accepted = targetScore >= threshold;
    const result: ScoreResult = {
      phonemes: target.phonemes.map((phoneme, i) =>
        i === index
          ? { phoneme, score: targetScore, ...(accepted || !heardAs ? {} : { heardAs }) }
          : { phoneme, score: 0.9 },
      ),
      targetIndex: index,
      targetScore,
      accepted,
      nearMiss: !accepted && targetScore >= threshold * 0.7,
      threshold,
      heard: true,
      heardSounds: target.phonemes.length,
    };
    const utterance: Utterance = {
      audio: new Float32Array(8000),
      durationMs: 500,
      cut: false,
      peakDb: this.childPeakDb,
      noiseDb: this.childPeakDb - 40,
      clippedShare: 0,
      bargeIn: false,
    };
    this.clearSilence();
    this.deps.voice.stop();
    this.endTalking();
    this.update({ phase: 'checking' });
    await this.feedback(result, utterance, this.epoch);
  }

  /** Debug: the child said nothing (the re-prompt after silence). */
  async debugSilence(): Promise<void> {
    if (this.snapshot.phase === 'complete' || this.coach.finished) return;
    await this.say(this.coach.silence(), this.epoch);
  }

  /** Debug: ends the mission now (then the codename stage or the report, as usual). */
  async debugEnd(): Promise<void> {
    if (this.snapshot.transmitting || this.coach.finished) return;
    await this.say(this.coach.endNow(), this.epoch);
    if (this.snapshot.phase !== 'complete') {
      await this.stopMic();
      this.update({ phase: 'complete' });
    }
  }

  /** "Hear it again" on the mouth picture: the cue once more, step by step, then the word. */
  async replayCue(): Promise<void> {
    if (this.snapshot.phase === 'hearing' || this.snapshot.phase === 'checking') return;
    const turn = this.coach.replayCue();
    if (!turn) return;
    if (this.micOn) await this.say(turn, this.epoch);
    else await this.deps.voice.play(this.voiceParts(turn.parts), this.captionHooks());
  }

  /** Captions that follow the voice, page by page, for speech outside a turn (microphone off). */
  private captionHooks() {
    return {
      onPart: (part: VoicePart, seconds?: number) => {
        if (part.kind === 'say') {
          this.followCaption(stripTags(part.text), seconds);
          this.followStep(part.text);
        }
      },
    };
  }

  /** The child asked for help ("show me how"): a cue with its diagram and a model. */
  async help(): Promise<void> {
    if (this.snapshot.phase === 'hearing' || this.snapshot.phase === 'checking') return;
    qa.event('note', { note: 'help requested' });
    if (!this.micOn) {
      const turn = this.coach.help();
      this.update({
        hint: turn.hint,
        caption: firstPage(turn.parts) ?? turn.caption,
        exercise: turn.exercise,
      });
      await this.deps.voice.play(this.voiceParts(turn.parts), this.captionHooks());
      return;
    }
    await this.say(this.coach.help(), this.epoch);
  }

  async hearWord(): Promise<void> {
    // Not while the child speaks or an attempt is being judged; the answer comes first.
    if (this.snapshot.phase === 'hearing' || this.snapshot.phase === 'checking') return;
    if (!this.micOn) {
      await this.deps.voice.play([{ kind: 'model', text: this.snapshot.target.text }]);
      return;
    }
    await this.say(this.coach.model(), this.epoch);
  }

  dispose(): void {
    void this.stopMic();
    this.deps.scorer.dispose();
    this.listeners.clear();
    this.levelListeners.clear();
  }

  // --- turn-taking ---

  private speechStarted(bargeIn: boolean, epoch: number, childFirst = false) {
    if (epoch !== this.epoch) return;
    // A question is open: it is answered by tapping, so talk does not start an attempt.
    if (this.snapshot.phase === 'answering') return;
    const now = performance.now();
    if (this.lastAttempt?.accepted && now - this.lastAttempt.at < REPEAT_WINDOW_MS) {
      // "Rah! Rah!": the child repeats a success. Let the praise play.
      this.ignoringRepeat = true;
      return;
    }
    this.ignoringRepeat = false;
    this.speechStartedAt = now;
    this.clearSilence();
    if (this.held) {
      // A second try right after a miss: wait for it before giving feedback.
      clearTimeout(this.held.timer);
      this.update({ phase: 'hearing' });
      return;
    }
    if (bargeIn && this.talking && this.resuming) {
      // Echo cut this turn off once already: keep talking; the utterance decides (see above).
      return;
    }
    if ((bargeIn || childFirst) && this.talking) {
      // The child started talking over the app, or had started just before it: stop and listen. The app is silent from now on,
      // which the listener uses to tell the child from the app's own voice.
      const playing = this.playing;
      this.interrupted = playing?.parts.slice(Math.max(0, playing.started - 1));
      this.deps.voice.stop();
      this.endTalking();
      this.listener?.setPrompting(false);
    }
    if (this.snapshot.phase !== 'checking') this.update({ phase: 'hearing' });
  }

  private async utterance(utterance: Utterance, epoch: number) {
    if (epoch !== this.epoch) return;
    if (this.snapshot.noiseQuestion) {
      // The noise question is answered by voice ("yes", "no, here is fine"), also over its own
      // line; never an attempt at the word. The background it asks about must not answer it:
      // only the child's own level, a clear peak, and a short answer count.
      const background =
        utterance.peakDb < this.childPeakDb - BACKGROUND_BELOW_CHILD_DB ||
        (utterance.cut && utterance.peakDb - utterance.noiseDb < STEADY_NOISE_SNR_DB);
      if (!background) void this.spokenNoiseAnswer(utterance.audio, epoch);
      if (this.snapshot.phase === 'hearing')
        this.update({ phase: this.talking ? 'talking' : 'answering' });
      return;
    }
    if (this.snapshot.phase === 'answering') return;
    if (this.ignoringRepeat) {
      this.ignoringRepeat = false;
      return;
    }
    if (utterance.cut && utterance.peakDb - utterance.noiseDb < STEADY_NOISE_SNR_DB) {
      // Seconds of low-level sound without a clear peak: the room (a fan, traffic), not a word.
      qa.event('discarded', {
        reason: 'steady-noise',
        snrDb: utterance.peakDb - utterance.noiseDb,
      });
      this.notAnAttempt(epoch);
      return;
    }
    if (utterance.peakDb < this.childPeakDb - BACKGROUND_BELOW_CHILD_DB) {
      // Much quieter than the child usually is: someone or something further away (a TV). It
      // does not count as the child speaking, so the wait for the child (and its re-prompts and
      // inactivity pause) carries on.
      this.backgroundDrops++;
      qa.event('discarded', {
        reason: 'background',
        peakDb: utterance.peakDb,
        childPeakDb: this.childPeakDb,
      });
      this.notAnAttempt(epoch, true);
      if (this.backgroundDrops >= BACKGROUND_HINT_AFTER && this.snapshot.phase === 'listening')
        this.update({ caption: TOO_QUIET_HINT });
      return;
    }
    const inEchoTail =
      this.speechStartedAt >= this.talkEndedAt &&
      this.speechStartedAt - this.talkEndedAt < ECHO_TAIL_MS;
    if (
      (utterance.bargeIn || inEchoTail) &&
      utterance.peakDb < this.childPeakDb - BARGE_IN_BELOW_CHILD_DB
    ) {
      qa.event('discarded', {
        reason: 'echo-level',
        peakDb: utterance.peakDb,
        childPeakDb: this.childPeakDb,
      });
      this.notAnAttempt(epoch);
      return;
    }
    this.interrupted = undefined;
    if (this.trying) {
      // The child made the cue step's sound ("ee"): the cue goes on. Not judged.
      qa.event('note', { note: `cue step tried: ${this.snapshot.trying ?? ''}` });
      this.finishTry(epoch);
      return;
    }
    if (this.snapshot.phase === 'checking') return;
    // Speech that began before the current feedback (a quick repeat while the last attempt was
    // being judged) does not cut the feedback short; the child gets the next turn after it.
    if (this.snapshot.phase === 'talking' && this.speechStartedAt < this.turnStartedAt) return;
    this.clearSilence();
    this.deps.voice.stop();
    this.endTalking();
    this.listener?.setPrompting(false);
    this.update({ phase: 'checking' });
    if (this.coach.choices) {
      // Choosing the next word by saying it: the word whose sounds the attempt matches best
      // (both share the /r/, so the other sounds decide). The same attempt is then judged.
      try {
        await this.resolveChoice(utterance.audio);
      } catch (error) {
        if (epoch === this.epoch)
          this.update({ phase: 'error', error: `Scoring failed: ${String(error)}` });
        return;
      }
      if (epoch !== this.epoch) return;
    }
    const target = this.coach.target;
    let result: ScoreResult;
    try {
      const score = (audio: Float32Array) =>
        this.deps.scorer.score({
          audio,
          targetPhonemes: target.phonemes,
          kind: target.kind,
          ...(this.deps.young ? { young: true } : {}),
        });
      // The word said more than once without a long enough pause ("rah … rah"): each try is scored
      // and the best one judged, as for two tries in a row. The app's own voice is never in the
      // recording (the microphone is muted while it talks), and a quieter burst is not a try.
      const tries = splitTries(utterance.audio, utterance.noiseDb);
      if (tries) {
        let best: { result: ScoreResult; index: number } | undefined;
        for (const [index, t] of tries.entries()) {
          const r = await score(t.audio);
          if (!best || r.targetScore > best.result.targetScore) best = { result: r, index };
        }
        const chosen = tries[best?.index ?? 0];
        qa.event('split', {
          tries: tries.map((t) => [Math.round(t.startMs), Math.round(t.endMs)]),
          chosen: best?.index,
          wasCut: utterance.cut,
        });
        if (!best || !chosen) throw new Error('no tries scored');
        result = best.result;
        utterance = {
          ...utterance,
          audio: chosen.audio,
          durationMs: chosen.endMs - chosen.startMs,
          cut: false,
          peakDb: chosen.peakDb,
        };
      } else {
        result = await score(utterance.audio);
      }
    } catch (error) {
      if (epoch === this.epoch)
        this.update({ phase: 'error', error: `Scoring failed: ${String(error)}` });
      return;
    }
    if (epoch !== this.epoch) return;
    qa.event('score', {
      utterance: qa.utterance(utterance.audio),
      target: { kind: target.kind, text: target.text },
      accepted: result.accepted,
      nearMiss: result.nearMiss,
      heard: result.heard,
      targetScore: round3(result.targetScore),
      phonemes: result.phonemes.map((p) => ({ p: p.phoneme, s: round3(p.score), as: p.heardAs })),
      repeatOfHeld: this.held !== undefined,
    });
    const first = this.takeHeld();
    if (first) {
      // Two tries in a row: judge the better one.
      if (first.result.targetScore >= result.targetScore) {
        result = first.result;
        utterance = first.utterance;
      }
    } else if (!result.accepted) {
      const heldAt = performance.now();
      const answer = () => {
        if (!this.held || epoch !== this.epoch) return;
        if (
          this.listener?.hearingVoice?.() &&
          performance.now() - heldAt < REPEAT_WAIT_MAX_MS - REPEAT_POLL_MS
        ) {
          this.held.timer = setTimeout(answer, REPEAT_POLL_MS);
          return;
        }
        const held = this.takeHeld();
        if (held) void this.feedback(held.result, held.utterance, epoch);
      };
      this.held = { result, utterance, timer: setTimeout(answer, REPEAT_WAIT_MS) };
      return;
    }
    await this.feedback(result, utterance, epoch);
  }

  /**
   * What was heard was not the child (noise, echo, background talk): carry on as if nothing was
   * said. An attempt waiting for a repeat gets its feedback; a line it cut off is finished.
   */
  private notAnAttempt(epoch: number, background = false) {
    if (this.trying) {
      // Not the child's step sound: keep waiting a little longer for it.
      this.update({ phase: 'listening' });
      this.clearSilence();
      this.silenceTimer = setTimeout(() => {
        this.finishTry(epoch);
      }, TRY_GRACE_MS);
      return;
    }
    const held = this.takeHeld();
    const interrupted = this.interrupted;
    this.interrupted = undefined;
    if (held) void this.feedback(held.result, held.utterance, epoch);
    else if (interrupted && this.snapshot.phase === 'hearing') void this.resume(interrupted, epoch);
    else if (this.snapshot.phase === 'hearing') this.listen(background);
  }

  /** Releases a held attempt (cancelling its timer). */
  private takeHeld() {
    const held = this.held;
    if (held) clearTimeout(held.timer);
    this.held = undefined;
    return held;
  }

  /** Judges an attempt with the coach and speaks the feedback. */
  private async feedback(result: ScoreResult, utterance: Utterance, epoch: number) {
    if (epoch !== this.epoch) return;
    if (result.heard && !utterance.cut) {
      // The child's own loudness, learned from attempts the scorer could hear: quickly upwards
      // (the child is the loudest voice near the device), slowly downwards, so an odd quiet
      // attempt (or a louder moment of a TV) does not drag the background threshold down.
      const rate = utterance.peakDb > this.childPeakDb ? 0.5 : 0.1;
      this.childPeakDb += rate * (utterance.peakDb - this.childPeakDb);
      this.backgroundDrops = 0;
    }
    this.lastAttempt = { at: performance.now(), accepted: result.accepted };
    const target = this.coach.target;
    if (
      !result.accepted &&
      utterance.bargeIn &&
      this.overlapRetried !== `${target.kind}:${target.text}`
    ) {
      // The child spoke while the app was still talking, so the attempt has the app's voice in
      // it: not a fair judgement. Ask once more instead of counting a miss.
      this.overlapRetried = `${target.kind}:${target.text}`;
      qa.event('discarded', { reason: 'overlap-retry', targetScore: result.targetScore });
      await this.say(this.coach.prompt(), epoch);
      return;
    }
    this.lastAudio = utterance.audio;
    const attempted = { word: this.coach.word, target: this.coach.target };
    const turn = this.coach.attempt({
      result,
      cut: utterance.cut,
      peakDb: utterance.peakDb,
      noiseDb: utterance.noiseDb,
      clippedShare: utterance.clippedShare,
    });
    const judged =
      turn.outcome === 'correct' ||
      turn.outcome === 'closer' ||
      turn.outcome === 'not-yet' ||
      turn.outcome === 'move-on' ||
      turn.outcome === 'session-end';
    if (judged) this.update({ result });
    await this.say(turn, epoch, attempted);
  }

  /**
   * Speaks a coach turn, then hands the turn back to the child (or pauses or ends). When the turn
   * moves to a new word, the attempted word and its verdict stay on screen during the feedback and
   * the new word appears with its prompt (after the turn's first pause).
   */
  private async say(
    turn: CoachTurn,
    epoch: number,
    attempted?: { word: CoachWord; target: PracticeTarget },
  ) {
    const serial = ++this.turnSerial;
    this.turnStartedAt = performance.now();
    this.clearSilence();
    const reward = turn.reward;
    const holdWord = turn.advanced && attempted !== undefined && turn.outcome !== 'session-end';
    let showingNext = !holdWord;
    const showNext = () => {
      if (showingNext || epoch !== this.epoch || serial !== this.turnSerial) return;
      showingNext = true;
      this.update({
        word: this.coach.word,
        target: turn.target,
        exercise: turn.exercise,
        choices: turn.choices,
        result: null,
      });
    };
    const { head, step, rest } = splitAtTry(turn.parts);
    this.update({
      phase: 'talking',
      caption: firstPage(turn.parts) ?? turn.caption,
      selfCheck: turn.selfCheck,
      noiseQuestion: turn.noiseQuestion,
      // The answers appear once the child has heard their playback.
      answerable: false,
      trying: null,
      hint: turn.hint ?? (KEEPS_HINT.has(turn.outcome) ? this.snapshot.hint : null),
      hintStep: turn.hint ? null : this.snapshot.hintStep,
      lastOutcome: turn.outcome,
      ...(holdWord
        ? {
            word: attempted.word,
            target: attempted.target,
            // The attempted word stays on screen, answered.
            exercise: { ...this.snapshot.exercise, revealed: true },
            choices: null,
          }
        : {
            word: this.coach.word,
            target: turn.target,
            exercise: turn.exercise,
            choices: turn.choices,
            ...(turn.advanced && turn.outcome !== 'session-end' ? { result: null } : {}),
          }),
      ...(reward
        ? {
            lastReward: reward,
            rewardSerial: this.snapshot.rewardSerial + 1,
            successes: this.snapshot.successes + (reward === 'success' ? 1 : 0),
            progressCredits: this.snapshot.progressCredits + (reward === 'progress' ? 1 : 0),
          }
        : {}),
    });
    // Speech-to-text loads while the question is said, so a spoken answer is understood at once.
    if (turn.noiseQuestion) this.deps.prepareAnswers?.();
    this.listener?.setHangover(turn.hangoverMs);
    this.listener?.setPrompting(true);
    this.muteForTalk();
    this.talking = true;
    qa.event('turn', {
      outcome: turn.outcome,
      caption: turn.caption,
      target: { kind: turn.target.kind, text: turn.target.text },
      reward: turn.reward ?? null,
    });
    const playing = { parts: this.voiceParts(head), started: 0 };
    this.playing = playing;
    const outcome = await this.deps.voice.play(playing.parts, {
      onPart: (part, seconds) => {
        playing.started++;
        qa.event('voice', describePart(part));
        // The caption follows the voice: the line being said, or the word being modelled.
        if (epoch !== this.epoch || serial !== this.turnSerial) return;
        if (part.kind === 'pause' && holdWord) showNext();
        if (part.kind === 'say') {
          this.followCaption(stripTags(part.text), seconds);
          this.followStep(part.text);
        } else if (part.kind === 'model') this.followCaption(`“${part.text}”`);
      },
      onPlayback: (playing) => {
        this.mutedForPlayback = playing;
        this.applyMute();
        // The child heard their attempt: now they can say which word it sounded like.
        if (!playing && this.snapshot.selfCheck && serial === this.turnSerial)
          this.update({ answerable: true });
      },
    });
    qa.event('voice', { end: outcome });
    // A newer turn (or a mic-off) took over: it owns what happens next.
    if (epoch !== this.epoch || serial !== this.turnSerial) return;
    showNext();
    this.endTalking();
    this.listener?.setPrompting(false);
    if (outcome !== 'interrupted' && step) {
      this.startTry(step, rest, epoch);
      return;
    }
    if (turn.outcome === 'session-end') {
      await this.stopMic();
      this.update({ phase: 'complete', transmitted: this.coach.transmitted });
      return;
    }
    if (turn.outcome === 'mic-pause') {
      await this.stopMic();
      return;
    }
    // Interrupted by barge-in: the child is already speaking.
    if (outcome === 'interrupted' && this.snapshot.phase === 'hearing') return;
    if (this.snapshot.phase === 'talking') this.afterTalk();
  }

  /** The child's turn after the app talked: an answer to tap when a question is open, else speech. */
  private afterTalk() {
    if (this.coach.asking) {
      this.clearSilence();
      this.update({ phase: 'answering', answerable: true });
      return;
    }
    this.listen();
  }

  /** The child makes the cue step's sound now; the cue goes on when they have (or after a while). */
  private startTry(step: { text: string; ms: number }, rest: CoachPart[], epoch: number) {
    this.trying = { rest, epoch };
    this.clearSilence();
    this.update({ phase: 'listening', trying: step.text });
    qa.event('note', { note: `cue step: ${step.text}` });
    this.silenceTimer = setTimeout(() => {
      this.finishTry(epoch);
    }, step.ms);
  }

  private finishTry(epoch: number) {
    const trying = this.trying;
    if (!trying || epoch !== this.epoch) return;
    this.trying = undefined;
    this.clearSilence();
    this.update({ trying: null });
    void this.continueParts(trying.rest, epoch);
  }

  /** The rest of a turn after a cue step (it may hold another step). */
  private async continueParts(parts: CoachPart[], epoch: number) {
    const { head, step, rest } = splitAtTry(parts);
    const serial = ++this.turnSerial;
    this.turnStartedAt = performance.now();
    this.update({ phase: 'talking' });
    this.listener?.setPrompting(true);
    this.muteForTalk();
    this.talking = true;
    const playing = { parts: this.voiceParts(head), started: 0 };
    this.playing = playing;
    const outcome = await this.deps.voice.play(playing.parts, {
      onPart: (part, seconds) => {
        playing.started++;
        qa.event('voice', describePart(part));
        if (epoch !== this.epoch || serial !== this.turnSerial) return;
        if (part.kind === 'say') {
          this.followCaption(stripTags(part.text), seconds);
          this.followStep(part.text);
        } else if (part.kind === 'model') this.followCaption(`“${part.text}”`);
      },
    });
    if (epoch !== this.epoch || serial !== this.turnSerial) return;
    this.endTalking();
    this.listener?.setPrompting(false);
    if (outcome !== 'interrupted' && step) {
      this.startTry(step, rest, epoch);
      return;
    }
    if (outcome === 'interrupted' && this.snapshot.phase === 'hearing') return;
    if (this.snapshot.phase === 'talking') this.afterTalk();
  }

  /** The mouth picture highlights the cue step being said. */
  private followStep(text: string) {
    const steps = this.snapshot.hint?.steps;
    if (!steps) return;
    const index = steps.findIndex((s) => s.text === stripTags(text));
    if (index >= 0) this.update({ hintStep: index });
  }

  /** Speaks the rest of a turn that the app's own echo (or noise) cut off, then listens. */
  private async resume(parts: VoicePart[], epoch: number) {
    const serial = ++this.turnSerial;
    this.turnStartedAt = performance.now();
    this.update({ phase: 'talking' });
    this.listener?.setPrompting(true);
    this.muteForTalk();
    this.talking = true;
    const playing = { parts, started: 0 };
    this.playing = playing;
    this.resuming = true;
    qa.event('turn', { outcome: 'resume', parts: parts.map(describePart) });
    const outcome = await this.deps.voice.play(parts, {
      onPart: (part, seconds) => {
        playing.started++;
        qa.event('voice', describePart(part));
        if (epoch !== this.epoch || serial !== this.turnSerial) return;
        if (part.kind === 'say') this.followCaption(stripTags(part.text), seconds);
        else if (part.kind === 'model') this.followCaption(`“${part.text}”`);
      },
      onPlayback: (on) => {
        this.mutedForPlayback = on;
        this.applyMute();
      },
    });
    this.resuming = false;
    qa.event('voice', { end: outcome });
    if (epoch !== this.epoch || serial !== this.turnSerial) return;
    this.endTalking();
    this.listener?.setPrompting(false, false);
    if (outcome === 'interrupted' && this.snapshot.phase === 'hearing') return;
    if (this.snapshot.phase === 'talking') this.afterTalk();
  }

  /**
   * The child's turn. `resumeWait` continues the current wait (after background sound that was
   * not the child) instead of starting a new one, so a TV cannot keep the microphone on forever.
   */
  private listen(resumeWait = false) {
    this.update({ phase: 'listening' });
    this.clearSilence();
    const epoch = this.epoch;
    const now = performance.now();
    if (!resumeWait || this.silenceDeadline <= 0)
      this.silenceDeadline = now + (this.deps.silenceMs ?? DEFAULT_SILENCE_MS);
    let postponed = 0;
    const fire = () => {
      if (epoch !== this.epoch || this.snapshot.phase !== 'listening') return;
      // The child may be just starting to answer: a re-prompt now would talk over the word and
      // make it look like a barge-in. Wait a moment (bounded, so a loud room cannot hold it off).
      if (this.listener?.hearingVoice?.() && postponed < SILENCE_POSTPONE_MAX) {
        postponed++;
        this.silenceTimer = setTimeout(fire, SILENCE_POSTPONE_MS);
        return;
      }
      void this.say(this.coach.silence(), epoch);
    };
    this.silenceTimer = setTimeout(fire, Math.max(0, this.silenceDeadline - now));
  }

  private voiceParts(parts: readonly CoachPart[]): VoicePart[] {
    return parts.flatMap((part): VoicePart[] => {
      // Cue steps are the session's (startTry); without a microphone they are skipped.
      if (part.kind === 'try') return [];
      if (part.kind !== 'playback') return [part];
      return this.lastAudio ? [{ kind: 'playback', audio: this.lastAudio }] : [];
    });
  }

  /** The app's voice stopped (finished or cut off). */
  /** The app's voice stopped (finished or cut off): the microphone listens again. */
  private endTalking() {
    // A cut-off line leaves its caption where it stopped.
    for (const t of this.captionTimers) clearTimeout(t);
    this.captionTimers = [];
    if (this.talking) this.talkEndedAt = performance.now();
    this.talking = false;
    this.mutedForTalk = false;
    this.applyMute();
  }

  private muteForTalk() {
    this.mutedForTalk = true;
    this.applyMute();
  }

  private applyMute() {
    this.listener?.setMuted(this.mutedForTalk || this.mutedForPlayback);
  }

  private clearSilence() {
    if (this.silenceTimer !== undefined) clearTimeout(this.silenceTimer);
    this.silenceTimer = undefined;
  }

  private restart() {
    this.coach = this.newCoach();
    this.update({
      word: this.coach.word,
      target: this.coach.target,
      result: null,
      caption: '',
      hint: null,
      exercise: this.coach.exercise,
      choices: null,
      selfCheck: null,
      noiseQuestion: false,
      answerable: false,
      hintStep: null,
      trying: null,
      lastOutcome: null,
      successes: 0,
      progressCredits: 0,
      lastReward: null,
    });
  }

  private newCoach() {
    return new Coach({
      words: this.deps.words,
      sound: this.deps.sound,
      ...(this.deps.ageBand ? { ageBand: this.deps.ageBand } : {}),
      ...(this.deps.random ? { random: this.deps.random } : {}),
      ...(this.deps.exercises ? { exercises: this.deps.exercises } : {}),
      ...(this.deps.choices ? { choices: true } : {}),
      ...(this.deps.briefing ? { briefing: this.deps.briefing } : {}),
    });
  }

  private update(patch: Partial<SessionSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    if (qa.recording) qa.event('snapshot', compactSnapshot(this.snapshot));
    for (const listener of this.listeners) listener();
  }
}

/** A re-prompt waits while a voice is being heard, up to this many times this long. */
const SILENCE_POSTPONE_MS = 300;
const SILENCE_POSTPONE_MAX = 3;

/** Turns that do not judge an attempt keep the cue's diagram on screen. */
/** After a cue step's wait was broken by noise, how much longer to wait for the child's sound. */
const TRY_GRACE_MS = 2000;

/** The first caption page of a turn's first spoken line. */
function firstPage(parts: readonly CoachPart[]): string | undefined {
  const first = parts.find((p) => p.kind === 'say');
  return first?.kind === 'say' ? captionPages(stripTags(first.text))[0] : undefined;
}

/** A turn's parts up to its first cue step, that step, and the parts after it. */
function splitAtTry(parts: readonly CoachPart[]): {
  head: CoachPart[];
  step: { text: string; ms: number } | undefined;
  rest: CoachPart[];
} {
  const at = parts.findIndex((p) => p.kind === 'try');
  if (at < 0) return { head: [...parts], step: undefined, rest: [] };
  const step = parts[at];
  return {
    head: parts.slice(0, at),
    step: step?.kind === 'try' ? { text: step.text, ms: step.ms } : undefined,
    rest: parts.slice(at + 1),
  };
}

const KEEPS_HINT: ReadonlySet<Outcome> = new Set<Outcome>([
  'silence',
  'not-heard',
  'too-quiet',
  'too-loud',
  'too-long',
  'off-target',
]);

/** Shown (not spoken) when only background sound has been heard for a while. */
const TOO_QUIET_HINT = 'Too quiet? Come a little closer to the microphone.';

const round3 = (v: number) => Math.round(v * 1000) / 1000;

function describePart(part: VoicePart) {
  if (part.kind === 'pause') return { kind: 'pause', ms: part.ms };
  if (part.kind === 'playback') return { kind: 'playback', samples: part.audio.length };
  return { kind: part.kind, text: part.text };
}

/** What the QA log keeps of a snapshot. */
function compactSnapshot(s: SessionSnapshot) {
  return {
    phase: s.phase,
    word: s.word.id,
    target: `${s.target.kind}:${s.target.text}`,
    caption: s.caption,
    hint: s.hint,
    lastOutcome: s.lastOutcome,
    accepted: s.result?.accepted ?? null,
    targetScore: s.result ? round3(s.result.targetScore) : null,
    successes: s.successes,
    progressCredits: s.progressCredits,
    error: s.error,
  };
}

/** Characters that fit the caption box (three lines on a narrow phone). */
/**
 * Longest caption page: three lines of the feedback panel on a phone (its caption is clamped to
 * three; 110 characters fell off the card at a mission's start, QA 2026-10-11).
 */
export const CAPTION_PAGE = 80;
/** Speaking rate for timing captions of the browser voice (characters per second). */
const SPEECH_CHARS_PER_S = 14;

/**
 * A line split into pages of at most CAPTION_PAGE characters: at sentence ends, and a sentence
 * longer than a page at its commas and semicolons (where possible).
 */
export function captionPages(text: string): string[] {
  if (text.length <= CAPTION_PAGE) return [text];
  const sentences = text.match(/[^.!?…]+[.!?…]+[”"’)]*\s*|[^.!?…]+$/g) ?? [text];
  const pieces = sentences
    .map((s) => s.trim())
    .flatMap((sentence) =>
      sentence.length <= CAPTION_PAGE
        ? [sentence]
        : (sentence.match(/[^,;:]+[,;:]?\s*/g) ?? [sentence]).map((c) => c.trim()),
    );
  const pages: string[] = [];
  for (const sentence of pieces) {
    const last = pages.at(-1);
    if (last !== undefined && last.length + 1 + sentence.length <= CAPTION_PAGE)
      pages[pages.length - 1] = `${last} ${sentence}`;
    else pages.push(sentence);
  }
  return pages;
}
